
import React from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useSettings } from "@/context/SettingsContext";
import { Shield } from "lucide-react";
import { getRpgThemeClasses } from "@/lib/utils";

const PrivacyPolicy = () => {
  const { darkMode, enableRpgMode } = useSettings();
  const navigate = useNavigate();
  const rpgTheme = getRpgThemeClasses(enableRpgMode, darkMode);

  return (
    <div className={cn(
      "min-h-screen p-4 font-montserrat transition-colors duration-300",
      enableRpgMode ? (
        rpgTheme.background + " rpg-parchment"
      ) : (
        darkMode 
          ? "bg-gradient-to-br from-[#1a0930] to-[#3F2E46] text-[#FBF3F1]" 
          : "bg-gradient-to-br from-[#FBF3F1] to-[#D896A4]/10"
      )
    )}>
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/')}
            className={cn(
              enableRpgMode ? rpgTheme.text : (darkMode ? "text-[#FBF3F1]" : ""),
              enableRpgMode && "hover:bg-amber-600/20"
            )}
          >
            ← Back to App
          </Button>
        </div>

        <Card className={cn(
          "p-8 shadow-lg border backdrop-blur-sm",
          enableRpgMode ? (
            rpgTheme.cardBackground + " " + rpgTheme.text + " rpg-border"
          ) : (
            darkMode 
              ? "bg-[#2d1a45]/80 border-[#6127A0]/30 text-[#FBF3F1]" 
              : "bg-white/90 border-[#D896A4]/10"
          )
        )}>
          <div className="flex items-center mb-6">
            <Shield className={cn("h-6 w-6 mr-3", enableRpgMode ? "text-amber-500" : (darkMode ? "text-[#00FF99]" : "text-[#6127A0]"))} />
            <h1 className={cn("text-3xl font-bold", enableRpgMode && "drop-shadow-sm")}>
              Privacy Policy
            </h1>
          </div>

          <div className="space-y-6 text-sm leading-relaxed">
            <p className="text-xs text-muted-foreground">
              Last updated: {new Date().toLocaleDateString()}
            </p>

            <section>
              <h2 className={cn("text-xl font-semibold mb-3", enableRpgMode && "text-amber-600")}>
                1. Information We Collect
              </h2>
              <p className="mb-3">
                When you use Unstuck, we collect the following types of information:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Account Information:</strong> Email address and display name when you create an account</li>
                <li><strong>Journal Entries:</strong> Your personal journal content, including stuck points, responses to prompts, and reflections</li>
                <li><strong>Usage Data:</strong> Information about how you interact with our app, including dice rolls, session duration, and feature usage</li>
                <li><strong>Device Information:</strong> Browser type, device type, and basic technical information for app functionality</li>
              </ul>
            </section>

            <section>
              <h2 className={cn("text-xl font-semibold mb-3", enableRpgMode && "text-amber-600")}>
                2. How We Use Your Information
              </h2>
              <p className="mb-3">We use your information to:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Provide and maintain the journaling service</li>
                <li>Store and sync your journal entries across devices</li>
                <li>Authenticate your account and ensure security</li>
                <li>Improve app functionality and user experience</li>
                <li>Send important service-related communications</li>
              </ul>
            </section>

            <section>
              <h2 className={cn("text-xl font-semibold mb-3", enableRpgMode && "text-amber-600")}>
                3. Data Storage and Security
              </h2>
              <p className="mb-3">
                Your data is stored securely using Supabase, a trusted cloud database provider. We implement industry-standard security measures including:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Encryption of data in transit and at rest</li>
                <li>Regular security audits and updates</li>
                <li>Access controls and authentication protocols</li>
                <li>Backup and recovery procedures</li>
              </ul>
            </section>

            <section>
              <h2 className={cn("text-xl font-semibold mb-3", enableRpgMode && "text-amber-600")}>
                4. Your Rights
              </h2>
              <p className="mb-3">You have the right to:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Access:</strong> Request a copy of your personal data</li>
                <li><strong>Rectification:</strong> Correct inaccurate or incomplete data</li>
                <li><strong>Erasure:</strong> Request deletion of your account and data</li>
                <li><strong>Portability:</strong> Export your journal entries</li>
                <li><strong>Withdrawal:</strong> Withdraw consent for data processing</li>
              </ul>
            </section>

            <section>
              <h2 className={cn("text-xl font-semibold mb-3", enableRpgMode && "text-amber-600")}>
                5. Data Sharing
              </h2>
              <p>
                We do not sell, trade, or share your personal information with third parties except as necessary to provide our services (such as our hosting provider Supabase) or as required by law.
              </p>
            </section>

            <section>
              <h2 className={cn("text-xl font-semibold mb-3", enableRpgMode && "text-amber-600")}>
                6. Cookies and Analytics
              </h2>
              <p>
                We use essential cookies for app functionality and may use analytics to improve our service. You can manage cookie preferences in your browser settings.
              </p>
            </section>

            <section>
              <h2 className={cn("text-xl font-semibold mb-3", enableRpgMode && "text-amber-600")}>
                7. Contact Us
              </h2>
              <p>
                If you have questions about this Privacy Policy or want to exercise your rights, please contact us at{" "}
                <a 
                  href="mailto:hello@ameliastewart.co" 
                  className={cn(
                    "hover:underline",
                    enableRpgMode ? "text-amber-500 hover:text-amber-400" : (
                      darkMode ? "text-[#AE78F0] hover:text-[#c096ff]" : "text-[#6127A0] hover:text-[#4d1f80]"
                    )
                  )}
                >
                  hello@ameliastewart.co
                </a>
              </p>
            </section>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
